import React from "react";
import isEqual from "lodash/isEqual";
import Alert from "@cloudscape-design/components/alert";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";
import Checkbox from "@cloudscape-design/components/checkbox";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";

const contactOptions = [
	{ value: "phone", label: "Confirmed phone" },
	{ value: "telegram", label: "Telegram" },
	{ value: "vk", label: "VK" },
	{ value: "ok", label: "OK" },
	{ value: "gl", label: "Google" },
	{ value: "st", label: "Steam" },
];

const socialMethodOptions = [
	{ value: "vk", label: "VK" },
	{ value: "ok", label: "OK" },
	{ value: "gl", label: "Google" },
	{ value: "st", label: "Steam" },
	{ value: "tg", label: "Telegram" },
];

class CountrySettings extends React.Component {
	state = {
		countries: null,
		initialCountries: null,
		countryOptions: [],
		selectedCountry: null,
		selectedContact: null,
		loading: true,
		saving: false,
		error: "",
	};

	componentDidMount() {
		this.loadSettings();
	}

	loadSettings = () => {
		this.setState({ loading: true, error: "" });
		return this.props.Interface.request("get", { class: "data", module: this.props.module, method: "get" })
			.then(({ countries, countryOptions }) => {
				this.setState({ countries, initialCountries: countries, countryOptions, selectedCountry: null, selectedContact: null, loading: false });
			})
			.catch((error) => {
				this.setState({ loading: false, error: error.text || error.message || String(error) });
			});
	};

	hasChanges = () => {
		const { countries, initialCountries } = this.state;
		if (countries === null || initialCountries === null) return false;
		if (!this.props.social) return !isEqual(countries, initialCountries);

		const sortMethods = (rules) => Object.fromEntries(Object.entries(rules).map(([country, methods]) => [country, [...methods].sort()]));
		return !isEqual(sortMethods(countries), sortMethods(initialCountries));
	};

	addCountry = () => {
		const { selectedCountry, selectedContact } = this.state;
		this.setState((state) => ({
			countries: { ...state.countries, [selectedCountry.value]: this.props.social ? [] : selectedContact.value },
			selectedCountry: null,
			selectedContact: null,
		}));
	};

	setMethodHidden = (country, method, hidden) => {
		this.setState((state) => ({
			countries: { ...state.countries, [country]: hidden ? [...state.countries[country], method] : state.countries[country].filter((item) => item !== method) },
		}));
	};

	removeCountry = (country) => {
		this.setState((state) => {
			const countries = { ...state.countries };
			delete countries[country];
			return { countries };
		});
	};

	saveSettings = () => {
		this.setState({ saving: true, error: "" });
		return this.props.Interface.request("set", { class: "data", module: this.props.module, method: "save" }, { countries: this.state.countries })
			.then(({ countries }) => {
				this.setState({ countries, initialCountries: countries, saving: false });
				this.props.Interface.addNoty("Success", `${this.props.title} saved. Server changes take effect within 10 minutes; the website receives updates on page refresh or its next configuration check.`);
			})
			.catch((error) => {
				this.setState({ saving: false, error: error.text || error.message || String(error) });
			});
	};

	render() {
		const { countries, countryOptions, selectedCountry, selectedContact, loading, saving, error } = this.state;
		const disabled = loading || saving || countries === null;

		return (
			<SpaceBetween direction="vertical" size="m">
				{error && <Alert type="error">{error}</Alert>}
				<Button variant="primary" onClick={this.saveSettings} disabled={disabled || !this.hasChanges()} loading={saving}>Save {this.props.title}</Button>
				<Table
					loading={loading}
					trackBy="country"
					items={countries === null ? [] : Object.entries(countries).map(([country, contact]) => ({ country, contact }))}
					empty={countries === null ? "Settings have not been loaded" : "No country rules. Existing behavior is preserved."}
					columnDefinitions={[
						{ id: "country", header: "IP country", cell: (item) => item.country },
						{
							id: "contact",
							header: this.props.social ? "Hidden methods" : "Required contact",
							cell: (item) => this.props.social ? (
								<SpaceBetween direction="horizontal" size="s">
									{socialMethodOptions.map((method) => (
										<Checkbox key={method.value} disabled={disabled} checked={item.contact.includes(method.value)} onChange={({ detail }) => this.setMethodHidden(item.country, method.value, detail.checked)}>Hide {method.label}</Checkbox>
									))}
								</SpaceBetween>
							) : (
								<Select
									expandToViewport
									disabled={disabled}
									options={contactOptions}
									selectedOption={contactOptions.find((option) => option.value === item.contact)}
									onChange={({ detail }) => this.setState((state) => ({ countries: { ...state.countries, [item.country]: detail.selectedOption.value } }))}
								/>
							),
						},
						{ id: "actions", header: "Actions", cell: (item) => <Button disabled={disabled} onClick={() => this.removeCountry(item.country)}>Remove</Button> },
					]}
				/>
				<div style={{ display: "flex", alignItems: "flex-end", gap: "8px", flexWrap: "wrap" }}>
					<FormField label="IP country">
						<Select
							disabled={disabled}
							filteringType="auto"
							placeholder="Select country"
							options={countryOptions.filter((option) => countries === null || !Object.prototype.hasOwnProperty.call(countries, option.value))}
							selectedOption={selectedCountry}
							onChange={({ detail }) => this.setState({ selectedCountry: detail.selectedOption })}
						/>
					</FormField>
					{!this.props.social && <FormField label="Required contact">
						<Select disabled={disabled} placeholder="Select contact" options={contactOptions} selectedOption={selectedContact} onChange={({ detail }) => this.setState({ selectedContact: detail.selectedOption })} />
					</FormField>}
					<Button disabled={disabled || !selectedCountry || (!this.props.social && !selectedContact)} onClick={this.addCountry}>Add country</Button>
				</div>
			</SpaceBetween>
		);
	}
}

export default function WheelSettings({ Interface }) {
	return (
		<SpaceBetween direction="vertical" size="l">
			<Header variant="h1">GEO rules</Header>
			<Container header={<Header>Wheel contact</Header>}>
				<CountrySettings Interface={Interface} module="wheelSettings" title="wheel contact" />
			</Container>
			<Container header={<Header>Social login visibility</Header>}>
				<CountrySettings Interface={Interface} module="socialSettings" title="social login visibility" social />
			</Container>
		</SpaceBetween>
	);
}
